/**
 * Stage progression: enemy-count scaling, Challenging Stage detection,
 * and the cleared -> next-stage transition.
 *
 * Per-stage seed: baseSeed + stage. Maze, enemy spawns, flag positions are
 * all derived from this single seed via the generator. Replaying a run
 * therefore only needs (baseSeed, inputLog) — no per-stage RNG persistence.
 */

import type { GameConfig, GameState } from './types.ts';
import { generateMaze } from './maze.ts';
import { spawnCar } from './movement.ts';
import { createEnemies } from './enemies.ts';

/** Number of enemies on a given stage. Caps at 5. */
export function enemiesForStage(stage: number, config: GameConfig): number {
  const base = config.enemiesAtStart;
  const growth = Math.floor((stage - 1) / 2);
  return Math.min(5, base + growth);
}

/**
 * Fuel for a given stage. Stage 1 = fuelMax; each subsequent stage shaves
 * fuelStageFalloff ticks, floored at fuelMin. Returns a non-negative integer.
 */
export function fuelForStage(stage: number, config: GameConfig): number {
  const raw = config.fuelMax - Math.max(0, stage - 1) * config.fuelStageFalloff;
  return Math.max(config.fuelMin, raw);
}

/**
 * Enemy movement speed for a given stage. Stages below `enemySpeedBoostStage`
 * run at base; from that stage onward, enemies move at 2x base (still divides
 * HALF_TILE since base does). Keeps the constraint that subpixel positions
 * land exactly on tile centers.
 */
export function enemySpeedForStage(stage: number, config: GameConfig): number {
  return stage >= config.enemySpeedBoostStage ? config.enemySpeed * 2 : config.enemySpeed;
}

export function isChallengingStage(stage: number, config: GameConfig): boolean {
  return stage > 0 && stage % config.challengingStageEvery === 0;
}

/**
 * Player speed for this tick, including the speed-boost powerup if active.
 * Pure: returns an integer; rounding off the multiplier keeps positions on
 * the same subpixel grid even when speedBoostMultiplier is fractional.
 */
export function effectivePlayerSpeed(state: GameState): number {
  if (state.tick >= state.speedBoostUntil) return state.config.playerSpeed;
  return Math.max(1, Math.round(state.config.playerSpeed * state.config.speedBoostMultiplier));
}

/** True iff the speed boost is active this tick. */
export function isSpeedBoostActive(state: GameState): boolean {
  return state.tick < state.speedBoostUntil;
}

/** Apply the cleared -> next-stage transition if the timer has elapsed. */
export function tickStageTransition(state: GameState): GameState {
  if (state.phase !== 'cleared') return state;
  if (state.tick < state.nextStageAt) return state;
  return startStage(state, state.stage + 1);
}

/** Reset everything stage-local and roll into `nextStage`. Keeps run state. */
export function startStage(state: GameState, nextStage: number): GameState {
  const challenging = isChallengingStage(nextStage, state.config);
  const maze = generateMaze(state.baseSeed + nextStage);
  // AIDEV-NOTE: Challenging Stages have NO enemies, matching the original
  // Rally-X. Earlier this spawned the regular count and relied on stepEnemies
  // skipping them, but that left frozen cars stuck in corners.
  const enemyCount = challenging ? 0 : enemiesForStage(nextStage, state.config);
  return {
    ...state,
    stage: nextStage,
    challenging,
    maze,
    player: spawnCar(maze.spawn.x, maze.spawn.y),
    flags: maze.flags.map((f) => ({ tile: f.tile, kind: f.kind, collected: false })),
    enemies: createEnemies(maze, enemyCount, nextStage),
    smokes: [],
    chainStep: 0,
    scoreMultiplier: 1,
    speedBoostUntil: 0,
    yellowCollected: 0,
    fuel: fuelForStage(nextStage, state.config),
    phase: 'playing',
    respawnAt: 0,
    deathCause: null,
    nextStageAt: 0,
    stageStartTick: state.tick,
    events: [],
  };
}

/** Called when the 10th yellow is picked up; sets up the cleared -> next-stage timer. */
export function beginStageTransition(state: GameState): GameState {
  if (state.phase !== 'cleared') return state;
  if (state.nextStageAt > 0) return state;
  return {
    ...state,
    nextStageAt: state.tick + state.config.stageTransitionDuration,
  };
}
